
    // ── Import lid library ─────────────────────────────────────
    import {
      Algorithm,
      applyDither,
      renderToCanvas,
      downloadDithered,
      loadImageFile,
    } from "./lid.js";

    // ── DOM refs ───────────────────────────────────────────────
    const dropZone = document.getElementById("dropZone");
    const fileInput = document.getElementById("fileInput");
    const algorithmSelect = document.getElementById("algorithmSelect");
    const scaleSelect = document.getElementById("scaleSelect");
    const overlayColor = document.getElementById("overlayColor");
    const overlayOpacity = document.getElementById("overlayOpacity");
    const blendPresets = document.getElementById("blendPresets");
    const bgColor = document.getElementById("bgColor");
    const useGradient = document.getElementById("useGradient");
    const outputCanvas = document.getElementById("outputCanvas");
    const canvasArea = document.getElementById("canvasArea");
    const downloadBtn = document.getElementById("downloadBtn");
    const imageInfo = document.getElementById("imageInfo");
    const imageInfoText = document.getElementById("imageInfoText");

    // ── State ──────────────────────────────────────────────────
    let grayPixels = null;
    let width = 0;
    let height = 0;
    let currentBlendMode = "overlay";

    // ── Drag & Drop ────────────────────────────────────────────
    dropZone.addEventListener("dragover", (e) => {
      e.preventDefault();
      dropZone.classList.add("drag-over");
    });

    dropZone.addEventListener("dragleave", () => {
      dropZone.classList.remove("drag-over");
    });

    dropZone.addEventListener("drop", (e) => {
      e.preventDefault();
      dropZone.classList.remove("drag-over");
      const file = e.dataTransfer.files[0];
      if (file) loadImage(file);
    });

    fileInput.addEventListener("change", (e) => {
      const file = e.target.files[0];
      if (file) loadImage(file);
    });

    // ── Load Image ─────────────────────────────────────────────
    async function loadImage(file) {
      try {
        grayPixels = await loadImageFile(file);
        width = grayPixels.width;
        height = grayPixels.height;

        dropZone.classList.add("has-image");
        dropZone.querySelector("p").textContent = file.name;
        imageInfo.style.display = "block";
        imageInfoText.textContent = `${width}×${height} · ${(file.size / 1024).toFixed(1)} KB`;
        downloadBtn.disabled = false;

        render();
      } catch (err) {
        alert("Failed to load image: " + err.message);
      }
    }

    // ── Render ─────────────────────────────────────────────────
    function render() {
      if (!grayPixels) return;

      const algorithm = Algorithm[algorithmSelect.value.toUpperCase().replace(/-/g, "_")]
        || Algorithm.FLOYD_STEINBERG;

      const scaledWidth = width * parseInt(scaleSelect.value, 10);
      const scaledHeight = height * parseInt(scaleSelect.value, 10);

      outputCanvas.width = scaledWidth;
      outputCanvas.height = scaledHeight;
      outputCanvas.style.width = scaledWidth + "px";
      outputCanvas.style.height = scaledHeight + "px";

      const pixels = applyDither(grayPixels.pixels, width, height, algorithm);
      renderToCanvas(outputCanvas, pixels, width, height);

      applyStyling();
    }

    // ── CSS Styling ────────────────────────────────────────────
    function applyStyling() {
      const canvas = outputCanvas;
      const color = overlayColor.value;
      const opacity = overlayOpacity.value / 100;
      const bg = bgColor.value;
      const gradient = useGradient.checked;

      // Background
      canvasArea.style.background = bg;

      if (gradient) {
        const r1 = parseInt(color.slice(1, 3), 16);
        const g1 = parseInt(color.slice(3, 5), 16);
        const b1 = parseInt(color.slice(5, 7), 16);
        canvasArea.style.background = `
          linear-gradient(135deg,
            rgba(${r1}, ${g1}, ${b1}, ${opacity}) 0%,
            rgba(${r1}, ${g1}, ${b1}, ${opacity * 0.3}) 50%,
            transparent 100%
          )
        `;
      } else {
        canvasArea.style.background = `rgba(${parseInt(color.slice(1,3),16)}, ${parseInt(color.slice(3,5),16)}, ${parseInt(color.slice(5,7),16)}, ${opacity})`;
      }

      // Overlay canvas with blend mode
      canvas.style.mixBlendMode = currentBlendMode === "none" ? "normal" : currentBlendMode;
    }

    // ── Event Listeners ────────────────────────────────────────
    algorithmSelect.addEventListener("change", render);
    scaleSelect.addEventListener("change", render);
    overlayColor.addEventListener("input", applyStyling);
    overlayOpacity.addEventListener("input", applyStyling);
    bgColor.addEventListener("input", applyStyling);
    useGradient.addEventListener("change", applyStyling);

    blendPresets.addEventListener("click", (e) => {
      const btn = e.target.closest("button");
      if (!btn) return;
      blendPresets.querySelectorAll("button").forEach(b => b.classList.remove("active"));
      btn.classList.add("active");
      currentBlendMode = btn.dataset.mode;
      applyStyling();
    });

    downloadBtn.addEventListener("click", () => {
      if (grayPixels) {
        downloadDithered(outputCanvas, "lid-dithered.png");
      }
    });

    // ── Paste from clipboard ───────────────────────────────────
    document.addEventListener("paste", (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) loadImage(file);
          break;
        }
      }
    });
  